import { getUserProfileData } from "services/user.service";
import {
  DonateWidget,
  DonateWidgetType,
  getDonationGoalObsUrl,
} from "services/main/donate.service";
import type {
  AlertIconMode,
  AlertIconShape,
  AlertLayoutPreset,
  AlertTheme,
  LeaderboardPeriod,
  TimerLayout,
  WidgetConfigState,
  WidgetTab,
} from "./types";
import { DEFAULT_CONFIG, ENABLED_WIDGET_TABS, STORAGE_KEY, WIDGET_DEFINITIONS } from "./constants";
import { normalizeAlertLayoutPreset } from "./constants";

export const getUserData = getUserProfileData;

export const normalizeList = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
};

export const normalizeWidgetList = (response: any): DonateWidget[] =>
  normalizeList(response) as DonateWidget[];

export const getWidgetData = (response: any): DonateWidget =>
  (response?.data || response || {}) as DonateWidget;

export const getWidgetId = (widget?: DonateWidget) => widget?._id || widget?.id || "";

export const isNativeWidgetTab = (tab: WidgetTab) =>
  tab === "alert" || tab === "goal" || tab === "leaderboard";

export const findWidgetForTab = (widgets: DonateWidget[], tab: WidgetTab) => {
  const definition = WIDGET_DEFINITIONS[tab];
  const sameType = widgets.filter((widget) => widget?.type === definition.type);

  return (
    sameType.find((widget) => widget?.metadata?.widgetKey === tab) ||
    sameType.find((widget) => widget?.metadata?.tab === tab) ||
    sameType.find((widget) => widget?.metadata?.kind === tab) ||
    sameType.find((widget) => widget?.name === definition.name) ||
    sameType.find((widget) => isNativeWidgetTab(tab) && widget?.isDefault) ||
    (isNativeWidgetTab(tab) ? sameType[0] : undefined)
  );
};

export const getWidgetTab = (widget?: DonateWidget): WidgetTab => {
  const widgetKey = widget?.metadata?.widgetKey;
  if (
    widgetKey === "alert" ||
    widgetKey === "goal" ||
    widgetKey === "timer" ||
    widgetKey === "leaderboard" ||
    widgetKey === "chat"
  ) {
    return widgetKey;
  }

  if (widget?.type === "DONATION_ALERT") return "alert";
  if (widget?.type === "DONATION_GOAL") return "goal";
  if (widget?.type === "DONATION_LIST") return "leaderboard";
  return "timer";
};

export const isEnabledWidgetTab = (tab: WidgetTab) =>
  ENABLED_WIDGET_TABS.includes(tab);

export const isEnabledDonateWidget = (widget: DonateWidget) =>
  widget?.type === "DONATION_ALERT" || isEnabledWidgetTab(getWidgetTab(widget));

export const getWidgetTypeLabel = (type?: DonateWidgetType) => {
  if (type === "DONATION_ALERT") return "Donation Alert";
  if (type === "DONATION_GOAL") return "Donation Goal";
  if (type === "DONATION_LIST") return "Donation List";
  return "Custom";
};

export const getWidgetStatusVariant = (status?: string) =>
  status === "ACTIVE" ? "success" : "secondary";

export const formatWidgetDate = (value?: string) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("lo-LA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const toStringValue = (value: unknown, fallback: string) => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

export const toNumberValue = (value: unknown, fallback: number) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : fallback;
};

export const clampValue = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

export const toBooleanValue = (value: unknown, fallback: boolean) => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

export const getAlertDuration = (settings: Record<string, any>, fallback: string) => {
  if (settings.duration !== undefined) {
    return toStringValue(settings.duration, fallback);
  }

  const displayMs = Number(settings.displayMs);
  if (Number.isFinite(displayMs) && displayMs > 0) {
    return String(displayMs / 1000);
  }

  return fallback;
};

export const getWidgetPublicRouteUrl = (widget: DonateWidget | undefined) => {
  if (!widget?.publicKey) return "";

  const origin = window.location.origin || "https://donate.onetop.la";
  if (widget.type === "DONATION_ALERT" || getWidgetTab(widget) === "alert") {
    return `${origin}/widgets/alert/${encodeURIComponent(widget.publicKey)}`;
  }

  return `${origin}/widgets/${encodeURIComponent(widget.publicKey)}`;
};

export const getWidgetDisplayUrl = (
  widget: DonateWidget | undefined,
  fallback: string,
) => getWidgetPublicRouteUrl(widget) || widget?.widgetUrl || fallback;

export const getPaymentMethodValue = (payment: any) =>
  payment?._id || payment?.id || payment?.name || payment?.type || "";

export const getPaymentMethodLabel = (payment: any) =>
  payment?.name || payment?.type || payment?._id || payment?.id || "--";

export const formatCurrency = (amount: string | number) => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat("lo-LA").format(
    Number.isFinite(value) ? value : 0,
  );
};

export const readDraft = (): WidgetConfigState => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;

    const parsed = JSON.parse(raw);
    return {
      alert: { ...DEFAULT_CONFIG.alert, ...(parsed.alert || {}) },
      goal: { ...DEFAULT_CONFIG.goal, ...(parsed.goal || {}) },
      timer: { ...DEFAULT_CONFIG.timer, ...(parsed.timer || {}) },
      leaderboard: {
        ...DEFAULT_CONFIG.leaderboard,
        ...(parsed.leaderboard || {}),
      },
      chat: { ...DEFAULT_CONFIG.chat, ...(parsed.chat || {}) },
    };
  } catch {
    return DEFAULT_CONFIG;
  }
};

export const percentOfGoal = (current: string, target: string) => {
  const currentValue = Number(current || 0);
  const targetValue = Number(target || 0);
  if (!Number.isFinite(currentValue) || !Number.isFinite(targetValue)) return 0;
  if (targetValue <= 0) return 0;
  return Math.min(Math.max((currentValue / targetValue) * 100, 0), 100);
};

export const secondsToTimerLabel = (seconds: number) => {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const rest = safeSeconds % 60;

  return [hours, minutes, rest]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

export const getMaskedFallbackUrl = (path: string, username: string) => {
  const origin = window.location.origin || "https://donate.onetop.la";
  return `${origin}/widgets/${path}/${username || "streamer"}?token=********`;
};
